import { Controller } from "@hotwired/stimulus"

const PANEL_TRANSITION_MS = 260

export default class extends Controller {
  static targets = [
    "modal",
    "panel",
    "backdrop",
    "form",
    "title",
    "submitLabel",
    "methodOverride",
    "dateInput",
    "loggedAtInput",
    "entryTime",
    "label",
    "recentsSection",
    "recentsRail",
    "recentsPrev",
    "recentsNext",
    "calories",
    "protein",
    "fat",
    "carbs"
  ]

  static values = {
    createUrl: String,
    updateUrlTemplate: String
  }

  connect() {
    this.boundHandleKeydown = this.handleKeydown.bind(this)
    this.boundHandleRecentsScroll = this.handleRecentsScroll.bind(this)
    this.closeTimer = null
    this.isOpen = false
    this.localizeEntryTimes()
  }

  disconnect() {
    this.detachGlobalListeners()
    this.clearCloseTimer()
    document.body.classList.remove("overflow-hidden")
  }

  recentsRailTargetConnected(element) {
    element.addEventListener("scroll", this.boundHandleRecentsScroll, { passive: true })
    this.updateRecentsNavState()
  }

  recentsRailTargetDisconnected(element) {
    element.removeEventListener("scroll", this.boundHandleRecentsScroll)
  }

  open() {
    if (!this.hasModalTarget) return

    const wasHidden = this.modalTarget.classList.contains("hidden")
    this.clearCloseTimer()

    this.modalTarget.classList.remove("hidden")
    this.modalTarget.setAttribute("aria-hidden", "false")
    document.body.classList.add("overflow-hidden")
    this.attachGlobalListeners()
    this.isOpen = true

    if (wasHidden) {
      requestAnimationFrame(() => this.showPanelState())
    } else {
      this.showPanelState()
    }

    this.updateRecentsNavState()
    if (this.hasCaloriesTarget) this.caloriesTarget.focus()
  }

  openCreate() {
    this.resetForm()
    this.prepareCreate()
    this.open()
  }

  close() {
    if (!this.hasModalTarget || this.modalTarget.classList.contains("hidden")) return

    this.isOpen = false
    this.hidePanelState()
    this.detachGlobalListeners()
    this.clearCloseTimer()
    this.closeTimer = window.setTimeout(() => {
      if (this.isOpen) return
      this.modalTarget.classList.add("hidden")
      this.modalTarget.setAttribute("aria-hidden", "true")
      document.body.classList.remove("overflow-hidden")
    }, PANEL_TRANSITION_MS)
  }

  stopPropagation(event) {
    event.stopPropagation()
  }

  onSubmitEnd(event) {
    if (!event.detail.success) return
    this.resetForm()
    this.prepareCreate()
    this.localizeEntryTimes()
    this.updateRecentsNavState()
    if (this.hasCaloriesTarget) this.caloriesTarget.focus()
  }

  onDestroySubmitEnd(event) {
    if (!event.detail.success) return
    this.prepareCreate()
    this.localizeEntryTimes()
    this.updateRecentsNavState()
  }

  resetForm() {
    if (!this.hasFormTarget) return
    this.formTarget.reset()
  }

  openEdit(event) {
    const button = event.currentTarget
    const logId = button.dataset.logId
    if (!logId || !this.hasFormTarget) return

    const updateUrl = this.updateUrlTemplateValue.replace("__ID__", logId)
    this.formTarget.action = updateUrl
    if (this.hasMethodOverrideTarget) this.methodOverrideTarget.value = "patch"
    if (this.hasTitleTarget) this.titleTarget.textContent = "Edit Entry"
    if (this.hasSubmitLabelTarget) this.submitLabelTarget.textContent = "Update Entry"
    this.setRecentsVisible(false)

    if (this.hasDateInputTarget) this.dateInputTarget.value = button.dataset.date || ""
    if (this.hasLoggedAtInputTarget) {
      this.loggedAtInputTarget.value = this.formatDateTimeLocal(button.dataset.loggedAt)
    }
    if (this.hasLabelTarget) this.labelTarget.value = button.dataset.label || ""
    if (this.hasCaloriesTarget) this.caloriesTarget.value = button.dataset.calories || ""
    if (this.hasProteinTarget) this.proteinTarget.value = button.dataset.protein || ""
    if (this.hasFatTarget) this.fatTarget.value = button.dataset.fat || ""
    if (this.hasCarbsTarget) this.carbsTarget.value = button.dataset.carbs || ""

    this.open()
  }

  prepareCreate() {
    if (!this.hasFormTarget) return
    this.formTarget.action = this.createUrlValue
    if (this.hasMethodOverrideTarget) this.methodOverrideTarget.value = ""
    if (this.hasTitleTarget) this.titleTarget.textContent = "Add Entry"
    if (this.hasSubmitLabelTarget) this.submitLabelTarget.textContent = "Save Entry"
    this.setRecentsVisible(true)
  }

  applyRecent(event) {
    const button = event.currentTarget
    if (this.hasLabelTarget) this.labelTarget.value = button.dataset.recentLabel || ""
    if (this.hasCaloriesTarget) this.caloriesTarget.value = button.dataset.recentCalories || ""
    if (this.hasProteinTarget) this.proteinTarget.value = button.dataset.recentProtein || ""
    if (this.hasFatTarget) this.fatTarget.value = button.dataset.recentFat || ""
    if (this.hasCarbsTarget) this.carbsTarget.value = button.dataset.recentCarbs || ""
    if (this.hasCaloriesTarget) this.caloriesTarget.focus()
  }

  scrollRecents(event) {
    if (!this.hasRecentsRailTarget) return

    const direction = event.currentTarget.dataset.direction === "left" ? -1 : 1
    const delta = this.recentsRailTarget.clientWidth * 0.8 * direction
    this.recentsRailTarget.scrollBy({ left: delta, behavior: "smooth" })
    window.setTimeout(() => this.updateRecentsNavState(), 220)
  }

  setRecentsVisible(visible) {
    if (!this.hasRecentsSectionTarget) return
    this.recentsSectionTarget.classList.toggle("hidden", !visible)
    window.setTimeout(() => this.updateRecentsNavState(), 0)
  }

  entryTimeTargetConnected(element) {
    this.localizeEntryTime(element)
  }

  localizeEntryTimes() {
    if (!this.hasEntryTimeTarget) return
    this.entryTimeTargets.forEach((element) => this.localizeEntryTime(element))
  }

  localizeEntryTime(element) {
    const isoValue = element.dataset.isoTime
    if (!isoValue) return

    const date = new Date(isoValue)
    if (Number.isNaN(date.getTime())) return

    element.textContent = new Intl.DateTimeFormat(undefined, {
      hour: "numeric",
      minute: "2-digit",
    }).format(date)
  }

  formatDateTimeLocal(value) {
    if (!value) return ""
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return ""
    const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000)
    return local.toISOString().slice(0, 16)
  }

  attachGlobalListeners() {
    document.addEventListener("keydown", this.boundHandleKeydown)
  }

  detachGlobalListeners() {
    document.removeEventListener("keydown", this.boundHandleKeydown)
  }

  clearCloseTimer() {
    if (!this.closeTimer) return
    window.clearTimeout(this.closeTimer)
    this.closeTimer = null
  }

  showPanelState() {
    if (this.hasBackdropTarget) {
      this.backdropTarget.classList.remove("opacity-0")
      this.backdropTarget.classList.add("opacity-100")
    }
    if (!this.hasPanelTarget) return

    this.panelTarget.classList.remove("translate-y-full", "sm:translate-x-full")
    this.panelTarget.classList.add("translate-y-0", "sm:translate-x-0")
  }

  hidePanelState() {
    if (this.hasBackdropTarget) {
      this.backdropTarget.classList.remove("opacity-100")
      this.backdropTarget.classList.add("opacity-0")
    }
    if (!this.hasPanelTarget) return

    this.panelTarget.classList.remove("translate-y-0", "sm:translate-x-0")
    this.panelTarget.classList.add("translate-y-full", "sm:translate-x-full")
  }

  handleKeydown(event) {
    if (event.key === "Escape") {
      event.preventDefault()
      this.close()
    }
  }

  handleRecentsScroll() {
    this.updateRecentsNavState()
  }

  updateRecentsNavState() {
    if (!this.hasRecentsRailTarget || !this.hasRecentsPrevTarget || !this.hasRecentsNextTarget) return
    if (this.hasRecentsSectionTarget && this.recentsSectionTarget.classList.contains("hidden")) return

    const maxScroll = Math.max(0, this.recentsRailTarget.scrollWidth - this.recentsRailTarget.clientWidth)
    const left = this.recentsRailTarget.scrollLeft
    const atStart = left <= 2 || maxScroll <= 0
    const atEnd = left >= maxScroll - 2 || maxScroll <= 0

    this.setRecentsButtonState(this.recentsPrevTarget, atStart)
    this.setRecentsButtonState(this.recentsNextTarget, atEnd)
  }

  setRecentsButtonState(button, disabled) {
    button.disabled = disabled
    button.classList.toggle("opacity-35", disabled)
  }
}
